import React from 'react'
import styled from '@emotion/styled'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import { useStore } from '../../Store'
import { CREDIT_CARD_FEE } from '../../Constants/costs'
import BOECancellationPolicy from './BOECancellationPolicy'
import { COFCancellationPolicy } from '../COF/COFCancellationPolicy'
import { MXCancellationPolicy } from '../MX/MXCancellationPolicy'

const WhyBecomeAMemberContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background,
  boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
  justifySelf: 'stretch',
  margin: '0 3.5rem',
  padding: '2rem 4rem',
  borderBottom: `10px solid ${theme.palette.text.action}`,
  color: theme.palette.text.secondary,
  '& li': {
    fontSize: '12px',
  },
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    padding: '1rem 0.5rem',
    margin: '1rem 1rem 0 1rem',
  },
  [theme.breakpoints.only('sm')]: {
    boxShadow: 'none',
    borderRadius: '.5rem',
    margin: '1rem 1rem 0 1rem',
  },
}))

const InnerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1em',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    margin: '0 1rem',
  },
  [theme.breakpoints.only('sm')]: {
    height: '100%',
    backgroundColor: theme.palette.background,
    flex: 1,
  },
}))

const ListItem = styled('p')({
  fontSize: '1.1em',
  color: '#e0dede',
})

const SummaryContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '10px',
  width: '80%',
  color: (props) =>
    props.color ? theme.palette[props.color].main : theme.palette.text.primary,
  [theme.breakpoints.only('sm')]: {
    justifyContent: 'space-evenly',
  },
}))

const SpanStyled = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  [theme.breakpoints.down('xs')]: {
    fontSize: '0.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}))

const PerksContainer = styled(Box)(({ theme }) => ({
  flexDirection: 'row',
  display: 'flex',
  justifyContent: 'space-evenly',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

const H4Warper = styled('div')({
  marginTop: '1rem',
})

const H4Styled = styled('h4')(({ theme }) => ({
  margin: 0,
  fontSize: '1.5rem',
  marginBottom: '0.5rem',
}))

const DividerStyled = styled(Divider)(({ theme }) => ({
  margin: '0.5rem 0',
}))

function WhyBecomeAMember({
  withCost,
  isCOF,
  isMX,
  isBOE,
  lastStep,
  thirdParty = false,
}) {
  const {
    membershipRequest,
    setMembershipRequest,
    isMember,
    rushService,
    totalMxCost,
    totalMxProfit,
    totalBoeCost,
    totalCofCost,
    rushServiceCost,
    cstMembership,
    memberDiscount,
    setIsMember,
  } = useStore((state) => ({
    membershipRequest: state.membershipRequest,
    isMember: state.isMember,
    setIsMember: state.setIsMember,
    setMembershipRequest: state.setMembershipRequest,
    rushService: state.rushService,
    totalMxProfit: state.totalMxProfit,
    totalMxCost: state.totalMxCost,
    totalBoeCost: state.totalBoeCost,
    totalCofCost: state.totalCofCost,
    rushServiceCost: state.rushServiceCost,
    cstMembership: state.cstMembership,
    memberDiscount: state.memberDiscount,
  }))

  const handleMembershipRequest = () => {
    const prev = membershipRequest
    setMembershipRequest(!prev)
    setIsMember(!prev)
  }

  let AMOUNT = 0
  let AMOUNT_WITH_TAXES = 0
  let MEMBER_DISCOUNT = memberDiscount
  let REAL_MX_PROFIT = 0

  if (isCOF) {
    AMOUNT = membershipRequest ? totalCofCost - memberDiscount : totalCofCost
    AMOUNT_WITH_TAXES = AMOUNT
  } else if (isMX) {
    REAL_MX_PROFIT = membershipRequest
      ? Number(totalMxProfit) - memberDiscount
      : Number(totalMxProfit)
    const TOTAL_PROFIT_COST_CC = Number(totalMxCost) + Number(totalMxProfit)
    AMOUNT = membershipRequest
      ? TOTAL_PROFIT_COST_CC - memberDiscount
      : TOTAL_PROFIT_COST_CC
    AMOUNT_WITH_TAXES = AMOUNT * CREDIT_CARD_FEE
  } else if (isBOE) {
    AMOUNT = membershipRequest ? totalBoeCost - memberDiscount : totalBoeCost
    AMOUNT_WITH_TAXES = AMOUNT
  }

  const transactionAmount =
    AMOUNT_WITH_TAXES +
    Number(rushService && rushServiceCost) +
    Number(membershipRequest && cstMembership)

  return (
    <WhyBecomeAMemberContainer>
      {isBOE ? <BOECancellationPolicy /> : isCOF ? <COFCancellationPolicy /> : <MXCancellationPolicy />}
      {(isMember && !membershipRequest) || thirdParty ? null : (
        <InnerContainer>
          <H4Warper>
            <H4Styled>{`Why become a member?`}</H4Styled>
          </H4Warper>
          <PerksContainer>
            <ul>
              <li>Special pricing</li>
              <li>Video tutorials</li>
              <li>Online Pilot Forum</li>
              <li>Wether links</li>
              <li>Country-specific documents</li>
            </ul>
            <ul>
              <li>Airport information</li>
              <li>Route Calculator</li>
              <li>Fuel prices & AOE List</li>
              <li>Country-specific regulations</li>
            </ul>
          </PerksContainer>
          <ListItem>
            <strong>Basic Membership: ${`${cstMembership}`} / YEAR</strong>
          </ListItem>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleMembershipRequest}
            style={{ marginBottom: '20px', fontSize: '0.8rem' }}
          >
            {!membershipRequest
              ? 'Add membership to the cart'
              : 'Remove from cart'}
          </Button>
          <DividerStyled />
        </InnerContainer>
      )}
      {withCost && (
        <InnerContainer>
          {!isMX && (
            <SummaryContainer>
              <SpanStyled>Base fee:</SpanStyled>
              {isMember ? (
                <SpanStyled>
                  $ {(AMOUNT_WITH_TAXES + MEMBER_DISCOUNT).toFixed(2)}
                </SpanStyled>
              ) : (
                <SpanStyled>$ {AMOUNT_WITH_TAXES.toFixed(2) || 0}</SpanStyled>
              )}
            </SummaryContainer>
          )}
          {isMX && (
            <SummaryContainer>
              <SpanStyled>Airspace fees payment: </SpanStyled>
              {isMember ? (
                <SpanStyled>
                  $ {(REAL_MX_PROFIT + MEMBER_DISCOUNT).toFixed(2)}
                </SpanStyled>
              ) : (
                <SpanStyled>$ {REAL_MX_PROFIT.toFixed(2) || 0}</SpanStyled>
              )}
            </SummaryContainer>
          )}
          {isMX && (
            <SummaryContainer>
              <SpanStyled>Mexico Tax Payment:</SpanStyled>
              <SpanStyled>
                $ {Number(totalMxCost).toFixed(2) || 'error'}
              </SpanStyled>
            </SummaryContainer>
          )}
          {isMX && (
            <SummaryContainer>
              <SpanStyled>4% Credit Card Commission:</SpanStyled>
              <SpanStyled>
                $ {Number(AMOUNT_WITH_TAXES - AMOUNT).toFixed(2) || 'error'}
              </SpanStyled>
            </SummaryContainer>
          )}
          {rushService && (
            <SummaryContainer color="main">
              <SpanStyled>Rush Service Fee:</SpanStyled>
              <SpanStyled>$ {rushServiceCost.toFixed(2) || 'error'}</SpanStyled>
            </SummaryContainer>
          )}
          {membershipRequest && (
            <SummaryContainer>
              <SpanStyled>Membership:</SpanStyled>
              <SpanStyled>
                $ {Number(cstMembership).toFixed(2) || 'error'}
              </SpanStyled>
            </SummaryContainer>
          )}
          {isMember && MEMBER_DISCOUNT > 0 && (
            <SummaryContainer color="secondary">
              <SpanStyled>Member discount:</SpanStyled>
              <SpanStyled>
                - $ {MEMBER_DISCOUNT.toFixed(2) || 'error'}
              </SpanStyled>
            </SummaryContainer>
          )}
          <DividerStyled />
          <H4Warper>
            <H4Styled>{`TOTAL $${
              transactionAmount.toFixed(2) || 'error'
            } USD`}</H4Styled>
          </H4Warper>
          {!lastStep && (
            <SummaryContainer>
              <span style={{ textAlign: 'center' }}>
                You will see a summary in the next step before you are charged
              </span>
            </SummaryContainer>
          )}
        </InnerContainer>
      )}
    </WhyBecomeAMemberContainer>
  )
}

export default WhyBecomeAMember
